// src/DashboardFinancier.js
import React, { useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Cell
} from 'recharts';

const MOIS = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];

const STATUTS = [
  { key: 'gagne',    label: 'Gagné',    color: '#10b981' },
  { key: 'en_cours', label: 'En cours', color: '#3b82f6' },
  { key: 'prospect', label: 'Prospect', color: '#f59e0b' },
  { key: 'perdu',    label: 'Perdu',    color: '#ef4444' },
];

const fmt = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return Math.round(n / 1000) + 'k';
  return Math.round(n).toString();
};

const fmtFull = (n) => Math.round(n || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0') + ' FCFA';

const CustomTooltipCA = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
      padding: '10px 14px', fontSize: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <div style={{ fontWeight: 700, color: '#374151', marginBottom: '6px' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
          <span>{p.name}</span>
          <span style={{ fontWeight: 600 }}>{fmtFull(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const DashboardFinancier = ({ quotes = [] }) => {
  const now = new Date();
  const currentYear = now.getFullYear();

  // Données CA mensuel — tous les mois avec données
  const caParMois = useMemo(() => {
    const map = {};
    quotes.filter(q => q.statut === 'gagne').forEach(q => {
      const d = new Date(q.created_at);
      const key = d.getFullYear() + '-' + String(d.getMonth()).padStart(2, '0');
      const label = MOIS[d.getMonth()] + ' ' + d.getFullYear();
      if (!map[key]) map[key] = { key, label, ca: 0, nb: 0 };
      map[key].ca += q.total_with_vat || 0;
      map[key].nb += 1;
    });
    return Object.values(map).sort((a, b) => a.key.localeCompare(b.key));
  }, [quotes]);

  // CA cumulé annuel (année en cours)
  const caCumuleAnnuel = useMemo(() => {
    const moisAnnee = Array.from({ length: 12 }, (_, i) => ({
      label: MOIS[i],
      ca: 0,
      cumul: 0,
    }));
    quotes.filter(q => q.statut === 'gagne' && new Date(q.created_at).getFullYear() === currentYear)
      .forEach(q => {
        const m = new Date(q.created_at).getMonth();
        moisAnnee[m].ca += q.total_with_vat || 0;
      });
    let cumul = 0;
    moisAnnee.forEach(m => { cumul += m.ca; m.cumul = cumul; });
    // Ne garder que jusqu'au mois courant
    return moisAnnee.slice(0, now.getMonth() + 1);
  }, [quotes, currentYear, now]);

  // Pipeline par statut
  const pipeline = useMemo(() => {
    return STATUTS.map(s => ({
      ...s,
      nb: quotes.filter(q => q.statut === s.key).length,
      montant: quotes.filter(q => q.statut === s.key).reduce((sum, q) => sum + (q.total_with_vat || 0), 0),
    }));
  }, [quotes]);

  const totalPipeline = pipeline.reduce((s, p) => s + p.montant, 0);

  return (
    <div style={{ fontFamily: 'Segoe UI, sans-serif', display: 'flex', flexDirection: 'column', gap: '24px' }}>

      {/* Ligne 1 : CA mensuel + cumulé */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

        {/* CA mensuel barres */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px',
          border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e40af', marginBottom: '4px' }}>
            CA mensuel — Devis gagnés
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '16px' }}>
            Tous les mois avec au moins 1 vente
          </div>
          {caParMois.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '13px', padding: '40px 0' }}>
              Aucune vente enregistrée
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={caParMois} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#6b7280' }} />
                <YAxis tickFormatter={fmt} tick={{ fontSize: 10, fill: '#6b7280' }} width={45} />
                <Tooltip content={<CustomTooltipCA />} />
                <Bar dataKey="ca" name="CA" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                  {caParMois.map((entry, i) => (
                    <Cell key={i}
                      fill={entry.label.includes(String(currentYear)) && entry.label.includes(MOIS[now.getMonth()])
                        ? '#1e40af' : '#3b82f6'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* CA cumulé courbe */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px',
          border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e40af', marginBottom: '4px' }}>
            CA cumulé {currentYear}
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '16px' }}>
            Total cumulé depuis janvier {currentYear}
          </div>
          {caCumuleAnnuel.every(m => m.cumul === 0) ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '13px', padding: '40px 0' }}>
              Aucune vente en {currentYear}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={caCumuleAnnuel} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#6b7280' }} />
                <YAxis tickFormatter={fmt} tick={{ fontSize: 10, fill: '#6b7280' }} width={45} />
                <Tooltip content={<CustomTooltipCA />} />
                <Line type="monotone" dataKey="cumul" name="Cumulé" stroke="#10b981"
                  strokeWidth={2.5} dot={{ fill: '#10b981', r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Ligne 2 : Pipeline */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '20px',
        border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e40af', marginBottom: '4px' }}>
          Pipeline commercial
        </div>
        <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '20px' }}>
          Tous les devis — répartition par statut
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
          {pipeline.map(s => (
            <div key={s.key} style={{ background: s.color + '10', borderRadius: '10px',
              padding: '14px', border: '1px solid ' + s.color + '30', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: 800, color: s.color }}>{s.nb}</div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: s.color, marginTop: '2px' }}>{s.label}</div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>{fmtFull(s.montant)}</div>
            </div>
          ))}
        </div>

        {/* Barre de progression pipeline */}
        {totalPipeline > 0 && (
          <div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '6px' }}>
              Répartition du pipeline total — {fmtFull(totalPipeline)}
            </div>
            <div style={{ display: 'flex', height: '12px', borderRadius: '6px', overflow: 'hidden', gap: '1px' }}>
              {pipeline.filter(s => s.montant > 0).map(s => (
                <div key={s.key}
                  style={{ flex: s.montant / totalPipeline, background: s.color,
                    transition: 'flex 0.3s' }}
                  title={s.label + ' : ' + fmtFull(s.montant)} />
              ))}
            </div>
            <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
              {pipeline.filter(s => s.montant > 0).map(s => (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: s.color }} />
                  <span style={{ color: '#6b7280' }}>{s.label} — {Math.round(s.montant / totalPipeline * 100)}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardFinancier;
